import { FC, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Box, Paper } from "@material-ui/core";
import QRCode from "react-qr-code";
import { RootState } from "../../Redux/Reducers";
import { getDeviceInfo } from "../../Utils/deviceInfo";
import { initPaymentForQR, pollPayment, savePaymentInfo } from "../../Redux/Actions";
import history from "../../Utils/history";
import { useStyles } from "./style";
import GPayLogo from "../../Assets/images/gpay-logo.png";
import PaytmLogo from "../../Assets/images/paytm-logo.png";
import PhonePeLogo from "../../Assets/images/phonepe-logo.png";
import ellipLoader from "../../Assets/images/ellipLoader1.gif";
import refreshIcon from "../../Assets/images/twotone_refresh_white_24dp.png";
import { PAYMENT_TYPE_CONSTANTS, TRANSACTION_ID } from "../../Constants";
import { ToastNotifyError } from "../Toast";

const initialState = {
  otp: "",
  minutes: 3,
  seconds: 0,
  isTimeUp: false,
  qrText: "",
  showGenerateQr: true,
};

const QRPaymentComponent: FC<any> = (props: any) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  let timerID: any = null;
  const [state, setState] = useState(initialState);
  const [isTimerStarted, setIsTimerStarted] = useState(false);

  const { storeInfo, prePaymentData } = useSelector((state: RootState) => ({
    storeInfo: state.storeReducer.store,
    prePaymentData: state.paymentMethodReducer.prePaymentData,
  }));

  useEffect(() => {
    return () => {
      setIsTimerStarted(false);
      setState(initialState);
    };
  }, []);

  useEffect(() => {
    setState(initialState);
  }, [prePaymentData?.amount]);

  // useEffect for timer
  useEffect(() => {
    if (isTimerStarted) {
      timerID = setInterval(() => {
        _timer(state);
      }, 1000);
    }

    return () => {
      clearTimer();
    };
  }, [state.minutes, state.seconds, isTimerStarted]);

  const clearTimer = () => {
    timerID && clearInterval(timerID);
  };

  const QrPlaceholder = ({ children, classNameExtension }) => {
    return (
      <div className="relative">
        <QRCode size={150} value={""} />
        <div
          className={`bg-black-80 absolute w-100 top-0 left-0 right-0 bottom-0  flex justify-center items-center ${classNameExtension}`}
        >
          {children}
        </div>
      </div>
    );
  };

  const _renderQR = () => {
    if (state.isTimeUp) {
      return (
        <QrPlaceholder classNameExtension={"bg-black-90"}>
          <span className="flex justify-center items-center pb2 fw6 f5 white " onClick={createNewQR}>
            <img src={refreshIcon} className="mr2 img h2" />
            Refresh
          </span>
        </QrPlaceholder>
      );
    }

    if (state.showGenerateQr) {
      return (
        <QrPlaceholder classNameExtension={"bg-black-70"}>
          <button
            style={{ cursor: "pointer" }}
            className="pa1 bg-white br2 bw0 self-center-ns black"
            onClick={createNewQR}
          >
            Generate Code
          </button>
        </QrPlaceholder>
      );
    }

    return state.qrText ? (
      <QRCode size={150} value={state.qrText} />
    ) : (
      <QrPlaceholder classNameExtension={"bg-black-70"}>
        <img src={ellipLoader} className="h2 img" />
      </QrPlaceholder>
    );
  };

  const _renderQrTimerMessage = () => {
    return state.isTimeUp ? (
      <>
        <p className="f7 fw3">This QR code has expired</p>
      </>
    ) : (
      <p className="f7 fw3">
        This QR code will expire in 0{state.minutes}:{state.seconds < 10 ? `0${state.seconds}` : state.seconds} Minutes
      </p>
    );
  };

  /**
   * Function update the state for timer
   * @param {Object} state
   */
  const _timer = (state: { otp?: string; minutes: any; seconds: any; isTimeUp?: boolean; qrText?: string }) => {
    if (state.seconds > 0) {
      setState((preState) => ({
        ...preState,
        seconds: preState.seconds - 1,
      }));
    }
    if (state.seconds === 0) {
      if (state.minutes === 0) {
        clearTimer();
        setState((preState) => ({
          ...preState,
          isTimeUp: true,
        }));
      } else {
        setState((preState) => ({
          ...preState,
          minutes: preState.minutes - 1,
          seconds: 59,
        }));
      }
    }
  };

  /**
   * Function to initiate a payment and
   * set the state with the new QR data
   */
  const createNewQR = () => {
    setIsTimerStarted(true);
    setState((state) => ({
      ...state,
      showGenerateQr: false,
    }));
    const payload: any = {
      payment_option_id: 1,
      phone: storeInfo?.owner?.phone,
      orderID: prePaymentData?.orderID,
      paymentMethod: PAYMENT_TYPE_CONSTANTS.DUPI,
      merchantID: prePaymentData?.merchantID,
      deviceType: getDeviceInfo().OSName,
      browserType: getDeviceInfo().BrowserName,
      userAgent: getDeviceInfo().userAgent,
      source: "premium",
      merchantName: `Digital Showroom - ${storeInfo?.store_info?.name}`,
      credit_used: parseInt(props.creditsUsed),
    };

    dispatch(savePaymentInfo(payload));

    // Intiate payment
    dispatch(
      initPaymentForQR(payload, (_intPay: { status: any; intentLink: any }) => {
        // If payment initiated successfully
        if (_intPay.status) {
          setState((state) => ({
            ...state,
            minutes: 3,
            seconds: 0,
            isTimeUp: false,
            qrText: _intPay.intentLink,
            showGenerateQr: false,
          }));
          props.toggleInitiatePaymentApiState();
          // Initiate the payment varification process
          if (sessionStorage.getItem(TRANSACTION_ID)) {
            dispatch(
              pollPayment(sessionStorage.getItem(TRANSACTION_ID), history, (status) => {
                if (!status) {
                  setState(initialState);
                }
              }),
            );
          }
        } else {
          setState(initialState);
          clearTimer();
          ToastNotifyError(`error: ${"Unable to initiate payment!"}`);
        }
      }),
    );
    localStorage.setItem("orderID", "");
  };

  return (
    <Box display="flex" justifyContent="center" className="pv3 ph3">
      <div className="ph4 pt4 pb3 flex flex-column flex-auto">
        <section className="flex flex-column justify-center items-center">
          {_renderQR()}
          {isTimerStarted ? _renderQrTimerMessage() : <div className="mb4" />}
        </section>
        <section className="ba b--black-10 pv3 ph4 br3">
          <article className="tc fw5 f5 pb2 lh-copy">Scan this QR code with any of your UPI App and pay.</article>
          <article className="flex justify-center items-center">
            <img height="21" src={PaytmLogo} />
            <img height="23" src={GPayLogo} />
            <img height="20" src={PhonePeLogo} />
          </article>
        </section>
      </div>
    </Box>
  );
};

export default QRPaymentComponent;
